import { TPlantPDFReport } from '@/features/dashboard/components/admin/energy-report.tsx';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, LoaderIcon } from 'lucide-react';
import { useCSVExport } from '@/hooks/use-csv-export.ts';
import { ReactNode, useMemo } from 'react';
import { format } from 'date-fns';

interface PerformanceMonitoringExportCSVProps {
  startDate: Date;
  getPlantReport: () => TPlantPDFReport | undefined;
  children: (props: {
    onClick: () => void;
    isExporting: boolean;
    icon: ReactNode;
    text: string;
  }) => ReactNode;
}

const PerformanceMonitoringExportCSV = ({
  startDate,
  getPlantReport,
  children,
}: PerformanceMonitoringExportCSVProps) => {
  const { t } = useTranslation();
  const report = getPlantReport();

  const csvColumns = useMemo(
    () => [
      {
        key: 'metric',
        header: t('common.type.metric'),
        accessor: (item: { metric: string; value: number; unit: string }) => item.metric,
      },
      {
        key: 'value',
        header: t('common.type.value'),
        accessor: (item: { metric: string; value: number; unit: string }) => item.value,
      },
      {
        key: 'unit',
        header: t('common.type.unit'),
        accessor: (item: { metric: string; value: number; unit: string }) => item.unit,
      },
    ],
    [t]
  );

  const csvData = useMemo(() => {
    if (!report) return undefined;

    return [
      {
        metric: t('dashboard.energy_report.metrics.pv_production'),
        value: report.energyData.yield,
        unit: 'kWh',
      },
      {
        metric: t('dashboard.energy_report.metrics.total_consumption'),
        value: report.energyData.consumed,
        unit: 'kWh',
      },
      {
        metric: t('dashboard.energy_report.metrics.fed_to_grid'),
        value: report.energyData.fedToGrid,
        unit: 'kWh',
      },
      {
        metric: t('dashboard.energy_report.metrics.consumed_from_grid'),
        value: report.energyData.consumedFromGrid,
        unit: 'kWh',
      },
      {
        metric: t('dashboard.energy_report.metrics.battery_charge'),
        value: report.energyData.batteryCharge,
        unit: 'kWh',
      },
      {
        metric: t('dashboard.energy_report.metrics.battery_discharge'),
        value: report.energyData.batteryDischarge,
        unit: 'kWh',
      },
      {
        metric: t('dashboard.energy_report.metrics.battery_cycles'),
        value: report.energyData.batteryCycles,
        unit: '',
      },
    ];
  }, [report, t]);

  const { downloadCSV, isExporting } = useCSVExport({
    data: csvData,
    columns: csvColumns,
    filename: `${report?.plantName ?? 'Report'} - ${format(startDate, 'MMM d, yyyy')}`,
  });

  const icon = isExporting ? (
    <LoaderIcon className="size-4 animate-spin text-gray-800" aria-hidden="true" />
  ) : (
    <FileSpreadsheet className="size-4" />
  );

  const text: string = isExporting
    ? 'Generating...'
    : t('dashboard.performance_monitoring.generate_csv_report');

  return children({
    onClick: downloadCSV,
    isExporting,
    icon,
    text,
  });
};

export default PerformanceMonitoringExportCSV;
